import Sidebar from "@nkr/components/layout/sidebar";
import { DashReferralSourceApi, IDashParentReferralSource, IDashReferralSource } from "@nkr/models/dash";
import { RestApi } from "@nkr/utils/rest-api";
import useDebounce from "@nkr/utils/useDebounce";
import { FormEvent, useEffect, useState } from "react";
import { Alert, Well, Row, Col, FormControl, Button } from "react-bootstrap";

interface IReferralSourceSidebarProps {
    parents: IDashParentReferralSource[],
    sourceCodes: string[],
    selectedSource: IDashReferralSource,
    setSelectedSource: (source: IDashReferralSource | null) => void,
    pendingParentSelection: { parent: IDashParentReferralSource, nonce: number } | null,
}

interface IDonorConnectGroupOption {
    id: string,
    description: string,
}

const donorConnectGroupOptionsApi = new RestApi<IDonorConnectGroupOption, IDonorConnectGroupOption>(`${__API__}/dash/donor-connect-group-options`)
    .makeHooks();

const ReferralSourceSidebar = (props: IReferralSourceSidebarProps) => {
    const source = props.selectedSource;
    const createHook = DashReferralSourceApi.useCreate();
    const updateHook = DashReferralSourceApi.useUpdate(source.id || 0);
    const donorConnectGroupOptionsResult = donorConnectGroupOptionsApi.useGetAll();
    const [formData, setFormData] = useState<IDashReferralSource>(source);
    const [saveMessage, setSaveMessage] = useState("");
    const donorConnectGroupOptions = donorConnectGroupOptionsResult?.payload ?? [];
    const trimmedSrcCode = formData.srcCode?.trim() ?? "";

    const isSrcCodeAvailable = (code: string) => {
        const normalizedCode = code.trim().toLowerCase();
        const normalizedOriginalSrcCode = source.srcCode?.trim().toLowerCase() ?? "";

        if (!normalizedCode) {
            return false;
        }

        if (source.id > 0 && normalizedCode === normalizedOriginalSrcCode) {
            return true;
        }

        return !props.sourceCodes.some((existing) => existing.trim().toLowerCase() === normalizedCode);
    };

    const srcCodeValidation = useDebounce(trimmedSrcCode, 500, isSrcCodeAvailable) as { debouncedValue: string; isValid: boolean; isValidating: boolean };

    useEffect(() => {
        setFormData(source);
        setSaveMessage("");
    }, [source]);

    useEffect(() => {
        if (!props.pendingParentSelection) {
            return;
        }

        const nextSource = {
            ...formData,
            parentReferralSourceId: props.pendingParentSelection.parent.id,
            parentReferralSourceName: props.pendingParentSelection.parent.name,
        };

        setFormData(nextSource);
        props.setSelectedSource(nextSource);
    }, [props.pendingParentSelection?.nonce]);

    const handleDescriptionChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setFormData((current) => ({ 
            ...current, 
            [name]: value }));
    };

    const handleIntChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        const { name, value } = e.currentTarget;
        setFormData((current) => ({
            ...current,
            [name]: value === "" ? null : parseInt(value, 10),
        }));
    };

    const handleBoolChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        const { name, value } = e.currentTarget;
        setFormData((current) => ({
            ...current,
            [name]: value === "true",
        }));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!trimmedSrcCode || !srcCodeValidation.isValid || srcCodeValidation.isValidating) {
            return;
        }

        const payload = {
            ...formData,
            srcCode: trimmedSrcCode,
            description: formData.description?.trim(),
        };

        const result = source.id > 0
            ? await updateHook.submit(payload)
            : await createHook.submit(payload);

        if (!result?.isSuccess || !result.payload) {
            setSaveMessage(result?.message || "Unable to save referral source.");
            return;
        }

        setFormData(result.payload);
        setSaveMessage("");
        props.setSelectedSource(result.payload);
    };

    return (
        <Sidebar title="Referral Source" isOpen={true} onClose={() => props.setSelectedSource(null)}>
            <Well bsSize="small" style={{ minHeight: "calc(100vh - 90px)", marginBottom: 0 }}>
                {formData && (
                    <form onSubmit={handleSubmit}>
                        {!!saveMessage && (
                            <Alert bsStyle="warning" style={{ marginBottom: 10 }}>
                                {saveMessage}
                            </Alert>
                        )}
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12}><strong>SRC Code</strong></Col>
                            <Col xs={12}>
                                <FormControl
                                    name="srcCode"
                                    type="text"
                                    value={formData.srcCode ?? ""}
                                    onChange={handleDescriptionChange}
                                    placeholder="Enter SRC Code"
                                    style={{ borderColor: trimmedSrcCode && !srcCodeValidation.isValid ? "#a94442" : undefined }}
                                />
                                {trimmedSrcCode && srcCodeValidation.isValidating && (
                                    <div style={{ marginTop: 6, color: "#666" }}>Checking SRC Code...</div>
                                )}
                                {trimmedSrcCode && !srcCodeValidation.isValid && !srcCodeValidation.isValidating && (
                                    <Alert bsStyle="warning" style={{ marginTop: 8, marginBottom: 0, padding: "8px 12px" }}>
                                        This SRC Code already exists.
                                    </Alert>
                                )}
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12}><strong>Display Name</strong></Col>
                            <Col xs={12}><FormControl name="description" type="text" value={formData.description ?? ""} onChange={handleDescriptionChange} /></Col>
                        </Row>
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12}><strong>Parent Referral Source</strong></Col>
                            <Col xs={12}>
                                <FormControl componentClass="select" name="parentReferralSourceId" value={formData.parentReferralSourceId ?? ""} onChange={handleIntChange}>
                                    <option value="">None</option>
                                    {props.parents.map((parent) => (
                                        <option key={parent.id} value={parent.id}>{parent.name}</option>
                                    ))}
                                </FormControl>
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12}><strong>Donor Connect Group</strong></Col>
                            <Col xs={12}>
                                <FormControl componentClass="select" name="donorConnectGroup" value={formData.donorConnectGroup ?? ""} onChange={handleIntChange}>
                                    {donorConnectGroupOptions.map((option) => (
                                        <option key={option.id} value={option.id}>{option.description}</option>
                                    ))}
                                </FormControl>
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={12}>
                                <strong>Key Referrer</strong>
                                <FormControl componentClass="select" name="isKeyReferrer" value={String(formData.isKeyReferrer ?? false)} onChange={handleBoolChange}>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </FormControl>
                            </Col>
                            <Col xs={12}>
                                <strong>NKR Campaign</strong>
                                <FormControl componentClass="select" name="isNkrCampaign" value={String(formData.isNkrCampaign ?? false)} onChange={handleBoolChange}>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </FormControl>
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: 20 }}>
                            <Col xs={12}>
                                <Button
                                    bsStyle="primary"
                                    type="submit"
                                    disabled={createHook.isMutating || updateHook.isMutating || !trimmedSrcCode || !srcCodeValidation.isValid || srcCodeValidation.isValidating}
                                >
                                    {source.id > 0 ? "Update Referral Source" : "Create Referral Source"}
                                </Button>
                            </Col>
                        </Row>
                    </form>
                )}
            </Well>
        </Sidebar>
    );
};

export default ReferralSourceSidebar;